import { LoadingPage, tableClassNames, TableInner, TOnRowClick } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { useQueryListOneCompany } from '../../entities/companies/queries';
import { subtypeOptions } from '../../entities/giftPackages/formFields';
import { TGiftPackType, useQueryAllGiftPackages } from '../../entities/giftPackages/queries';
import { getReadableGiftPackageSubType, getReadableGiftPackageType } from '../../entities/giftPackages/shared';
import { TGiftPackageType } from '../../generated/graphql';
import { PACKAGE_PATH } from '../../routes/paths';

type TCompanyNameProps = {
  companyId: string;
};

export const CompanyName = (props: TCompanyNameProps) => {
  const { companyId } = props;

  const { data: company } = useQueryListOneCompany({ id: companyId });

  return <div className={clsx(tableClassNames.primary)}>{company ? <span>{company.name}</span> : <span>-</span>}</div>;
};

export const GiftPackages = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const navigate = useNavigate();
  const { data: giftPackages, loading, error } = useQueryAllGiftPackages();

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(giftPackages.map((pack) => pack.company)), 'id');
  }, [giftPackages]);

  const [companyFilter, setCompanyFilter] = useState<string>();
  const [typeFilter, setTypeFilter] = useState<string>();
  const [subTypeFilter, setSubTypeFilter] = useState<string>();

  const onRowClick: TOnRowClick<TGiftPackType> = useCallback(
    (row) => {
      navigate(generatePath(`${PACKAGE_PATH}`, { packageId: row.original.id }));
    },
    [navigate]
  );

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push(
      {
        disabled: companies.length === 0,
        label: 'Company',
        name: 'companyId',
        onChange: (v) => {
          setCompanyFilter(v != null ? (v as string) : undefined);
        },
        options: companies?.map((c) => ({ label: c.name, value: c.id })),
        type: 'singleSelect',
        value: companyFilter
      },
      {
        type: 'singleSelect',
        label: 'Package Gift Type',
        name: 'type',
        options: Object.values(TGiftPackageType).map((gp) => ({ label: getReadableGiftPackageType(gp), value: gp })),
        onChange: (v) => {
          setTypeFilter(v != null ? (v as string) : undefined);
        },
        value: typeFilter
      }
    );

    if (typeFilter && [TGiftPackageType.ChildBirthday, TGiftPackageType.WorkAnniversary].includes(typeFilter as TGiftPackageType))
      filtersList.push({
        type: 'singleSelect',
        label: 'SubType',
        name: 'subType',
        options: subtypeOptions[typeFilter] || [],
        onChange: (v) => {
          setSubTypeFilter(v != null ? (v as string) : undefined);
        },
        value: subTypeFilter
      });

    return filtersList;
  }, [companies, subTypeFilter, typeFilter, companyFilter]);

  const filteredGiftPackages = useMemo(() => {
    return giftPackages.filter((gp) => {
      if (companyFilter != null && gp.companyId !== companyFilter) return false;

      if (typeFilter != null && gp.type !== typeFilter) return false;

      if (subTypeFilter != null && gp.subType !== subTypeFilter) return false;

      return true;
    });
  }, [companyFilter, giftPackages, typeFilter, subTypeFilter]);

  //console.log('giftPackages?:', giftPackages);
  console.log('filteredGiftPackages?:', filteredGiftPackages);

  return (
    <TablePage dataLoading={loading} error={error} title='Gift Packages'>
      <Filters filters={filters} />
      {loading && <LoadingPage />}
      <div style={{ overflow: 'auto' }}>
        <TableInner<TGiftPackType>
          enableSorting={true}
          defaultSorting={{ id: 'name', desc: false }}
          columns={[
            {
              header: 'Company',
              accessorKey: 'company.name',
              cell: (cell) => <CompanyName companyId={cell.row.original.companyId as string} />
            },
            {
              header: 'Name',
              accessorKey: 'name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{cell.row.original.name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Type',
              accessorKey: 'type',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{getReadableGiftPackageType(cell.row.original.type)}</span>
                  </div>
                );
              }
            },
            {
              header: 'SubType',
              accessorKey: 'subType',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{getReadableGiftPackageSubType(cell.row.original.subType as string)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Products',
              accessorKey: 'products.length',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{cell.row.original.products.length}</span>
                  </div>
                );
              }
            }
          ]}
          data={filteredGiftPackages}
          emptyText='No packages were found.'
          onRowClick={onRowClick}
          hoverableRows={true}
          virtual={false}
        />
      </div>
    </TablePage>
  );
};
